import { NextSeo } from "next-seo";
import { useEffect, useRef, useState } from "react";
import HorseRacingHeader from "@/components/horseRacing/header";
import TrackList from "@/components/horseRacing/trackList";
import TrackListControls from "@/components/horseRacing/trackListControls";
import {
  RACING_SEARCH_RESULTS_RETURNED,
  RACING_TRACKS_LIST_PAGE_VIEWED,
} from "@/constants/amplitude";
import { SEO } from "@/constants/seo";
import { Hr_PastTrack } from "@/graphql/gql/graphql";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import useFilteredTracks from "@/utils/hooks/useFilteredTracks";
import styles from "./container.module.scss";

interface TrackListContainerProps {
  title: string;
  description: string;
  raceTracks: Hr_PastTrack[];
}

const Container = ({
  title,
  raceTracks,
  description,
}: TrackListContainerProps) => {
  const [view, setView] = useState<string>();
  const [searchTerm, setSearchTerm] = useState<string>();
  const { usaTracks, nonUsaTracks } = useFilteredTracks({
    tracks: raceTracks,
    searchTerm,
  });
  const { logAmplitudeEvent } = useAmplitude();
  const viewRef = useRef(view);

  useEffect(() => {
    if (viewRef.current !== view) {
      logAmplitudeEvent(RACING_TRACKS_LIST_PAGE_VIEWED, {
        View: view ?? "All Tracks",
      });
    }
    viewRef.current = view;
  }, [logAmplitudeEvent, view]);

  useEffect(() => {
    if (searchTerm !== undefined) {
      logAmplitudeEvent(RACING_SEARCH_RESULTS_RETURNED, {
        Module: "tracks_filters",
        "Search Results":
          Object.keys(usaTracks).length + Object.keys(nonUsaTracks).length,
        "Search Type": "racing_tracks",
      });
    }
  }, [logAmplitudeEvent, nonUsaTracks, searchTerm, usaTracks]);

  return (
    <div className={styles.container}>
      <NextSeo
        title={title}
        description={description}
        canonical={SEO.TRACK_BASE_URL}
      />
      <HorseRacingHeader title={title} />

      <TrackListControls setView={setView} setSearchTerm={setSearchTerm} />
      <TrackList
        searchTerm={searchTerm}
        view={view}
        usaTracks={usaTracks}
        nonUsaTracks={nonUsaTracks}
      />
    </div>
  );
};

export default Container;
